// File: src/components/GA4SetupPrompt.jsx
// A subtle, non-intrusive prompt encouraging users to connect Google Analytics
// Use on pages that show GA4-dependent features

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Info, X, ChevronRight, BarChart3 } from 'lucide-react';
import './GA4SetupPrompt.css';

/**
 * GA4SetupPrompt - A gentle nudge to connect Google Analytics
 * 
 * @param {string} variant - 'inline' (small banner) | 'tooltip' (icon with hover) | 'card' (larger callout)
 * @param {string} feature - Name of the feature that benefits from GA4 (e.g., "traffic sources", "SEO data")
 * @param {boolean} dismissible - Whether the prompt can be dismissed
 * @param {string} className - Additional CSS classes
 */
export default function GA4SetupPrompt({ 
  variant = 'inline', 
  feature = 'enhanced analytics',
  dismissible = true,
  className = ''
}) {
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);

  // Check localStorage for permanent dismissal
  const storageKey = `ga4_prompt_dismissed_${feature.replace(/\s+/g, '_')}`;
  const isPermanentlyDismissed = localStorage.getItem(storageKey) === 'true';

  if (dismissed || isPermanentlyDismissed) return null;

  const handleDismiss = (e) => {
    e.stopPropagation();
    setDismissed(true);
  };

  const handlePermanentDismiss = (e) => {
    e.stopPropagation();
    localStorage.setItem(storageKey, 'true');
    setDismissed(true);
  };

  const handleNavigate = () => {
    navigate('/integrations');
  };

  // Tooltip variant - just a small icon with hover info
  if (variant === 'tooltip') {
    return (
      <div 
        className={`ga4-prompt ga4-prompt--tooltip ${className}`}
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
      >
        <button className="ga4-prompt__icon-btn" aria-label="Google Analytics info">
          <Info size={16} />
        </button>
        
        {showTooltip && (
          <div className="ga4-prompt__tooltip">
            <p>Connect Google Analytics for {feature}</p>
            <button onClick={handleNavigate} className="ga4-prompt__tooltip-link">
              Set up now <ChevronRight size={14} />
            </button>
          </div>
        )}
      </div>
    );
  }

  // Card variant - larger callout for empty states
  if (variant === 'card') {
    return (
      <div className={`ga4-prompt ga4-prompt--card ${className}`}>
        {dismissible && (
          <button className="ga4-prompt__close" onClick={handleDismiss} aria-label="Dismiss">
            <X size={16} />
          </button>
        )}
        
        <div className="ga4-prompt__card-icon">
          <BarChart3 size={32} />
        </div>
        
        <div className="ga4-prompt__card-content">
          <h4>Unlock {feature}</h4>
          <p>
            Connect Google Analytics to see {feature}. It only takes a minute and you can 
            do it anytime from Settings.
          </p>
        </div>
        
        <div className="ga4-prompt__card-actions">
          <button className="ga4-prompt__btn ga4-prompt__btn--primary" onClick={handleNavigate}>
            Connect Analytics
            <ChevronRight size={16} />
          </button>
          {dismissible && (
            <button className="ga4-prompt__btn ga4-prompt__btn--text" onClick={handlePermanentDismiss}>
              Don't show again
            </button>
          )}
        </div>
      </div>
    );
  }

  // Default: Inline variant - subtle banner
  return (
    <div className={`ga4-prompt ga4-prompt--inline ${className}`}>
      <div className="ga4-prompt__inline-content">
        <Info size={16} className="ga4-prompt__inline-icon" />
        <span>
          Want {feature}?{' '}
          <button className="ga4-prompt__inline-link" onClick={handleNavigate}>
            Connect Google Analytics
          </button>
        </span>
      </div>
      
      {dismissible && (
        <button className="ga4-prompt__close" onClick={handleDismiss} aria-label="Dismiss">
          <X size={14} />
        </button>
      )}
    </div>
  );
}

/**
 * Wrapper component that only shows the prompt if GA4 is not connected
 * 
 * @param {boolean} isGA4Connected - Whether GA4 is connected
 * @param {React.ReactNode} children - The GA4SetupPrompt or other content to show when not connected
 */
export function GA4PromptWrapper({ isGA4Connected, children }) {
  if (isGA4Connected) return null;
  return <>{children}</>;
}